// The sittings in Ultime news (L1), connected: when the filter names sources
// of sittings (sittingScope), one list per chamber (GET /parliament/sittings,
// paged by cursor) stands in for the cards of those documents. Every list is
// asked again every minute while a sitting of the day is open in any list on
// the page, every five minutes otherwise, only in the foreground. A row leads
// to the sitting's page, which comes back here. Every element is one the
// design already has:
//   list heading   the heading and the date of a list, NewsSection.tsx:38-39
//   rows           the stages of an iter, NewsReader.tsx:129-139 (the open sitting as the current stage)
//   loading        TextLoading, news/NewsCard.tsx; «Carica altre» ListStatus.tsx; notices PanelStatus
import { useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useLocation } from "react-router";

import { keys } from "../../api/keys";
import { PanelStatus } from "../../components/PanelStatus";
import { TextLoading } from "../../components/news/NewsCard";
import { ROW_CLASS } from "../../components/news/NewsReader";
import { romeDay } from "../../lib/dates";
import { LoadMore } from "../news/ListStatus";
import {
  chamberName,
  pollingInterval,
  toSittingRows,
  updatedAtLabel,
  type Sitting,
  type SittingListScope,
  type SittingPage,
} from "./adapt";
import { useSittings } from "./api";

export const LOADING_SITTINGS = "Caricamento delle sedute…";
export const SITTINGS_FAILED = "Sedute non disponibili.";
export const MORE_SITTINGS_FAILED = "Altre sedute non disponibili.";
export const SITTINGS_REFRESH_FAILED = "Aggiornamento delle sedute non riuscito.";
export const NO_SITTINGS = "Nessuna seduta.";

/** The sittings of every page of a list, in order; [] before the first answer. */
const sittingsOf = (data: InfiniteData<SittingPage> | undefined): Sitting[] =>
  data?.pages.flatMap((page) => page.items) ?? [];

/**
 * The sittings of every list on the page (the active queries, not one left
 * in the cache by a filter of before): the lists share one rhythm, each
 * reads the others when its interval is computed.
 */
const cachedSittings = (queryClient: QueryClient): Sitting[] =>
  queryClient
    .getQueriesData<InfiniteData<SittingPage>>({ queryKey: keys.sittingsAll, type: "active" })
    .flatMap(([, data]) => sittingsOf(data));

/** The lists of sittings of a view, in the order of `lists` (the Camera's first). */
export function SittingLists({ lists }: { lists: SittingListScope[] }) {
  const { pathname, search } = useLocation();
  const from = `${pathname}${search}`;
  return (
    <>
      {lists.map((scope) => (
        <SittingList key={scope.chamber} scope={scope} from={from} />
      ))}
    </>
  );
}

/** One list: a chamber's sittings of the type(s) in the filter, with the freshness and the status row under the rows. */
export function SittingList({ scope, from }: { scope: SittingListScope; from: string }) {
  const today = romeDay();
  const queryClient = useQueryClient();
  const query = useSittings(scope.chamber, scope.sessionType, (own) =>
    pollingInterval([...own, ...cachedSittings(queryClient)], today),
  );
  const sittings = useMemo(() => sittingsOf(query.data), [query.data]);
  const rows = useMemo(() => toSittingRows(sittings, today), [sittings, today]);
  // the rows of the last filter stand in while the new ones load: the list is ready, not loading
  const hasRows = query.data !== undefined;

  let status: React.ReactNode = null;
  if (!hasRows && (query.isPending || query.isFetching)) status = <TextLoading label={LOADING_SITTINGS} />;
  else if (!hasRows && query.isError)
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
        {SITTINGS_FAILED}
      </PanelStatus>
    );
  else if (query.isFetchNextPageError)
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.fetchNextPage()}>
        {MORE_SITTINGS_FAILED}
      </PanelStatus>
    );
  else if (query.isError && !query.isFetching)
    // a poll failed while the sittings of the last answer stay on the page, as REFRESH_FAILED of the lists of news
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
        {SITTINGS_REFRESH_FAILED}
      </PanelStatus>
    );
  else if (rows.length === 0)
    // an empty stand-in of the last filter says nothing about the new one
    status = query.isPlaceholderData ? (
      <TextLoading label={LOADING_SITTINGS} />
    ) : (
      <PanelStatus indent={false}>{NO_SITTINGS}</PanelStatus>
    );
  else if (query.hasNextPage)
    status = (
      <LoadMore
        isDisabled={query.isFetchingNextPage || query.isPlaceholderData}
        onPress={() => void query.fetchNextPage()}
      />
    );

  const updated = updatedAtLabel(sittings);
  const headingId = `sittings-${scope.chamber}`;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-[15px]">
      <header className="flex items-center justify-between">
        <h2 id={headingId} className="text-xl leading-7 font-semibold text-brand">
          {chamberName(scope.chamber)}
        </h2>
        {updated && <p className="text-right text-xs leading-5 font-medium text-muted">{updated}</p>}
      </header>
      {rows.length > 0 && (
        <ol>
          {rows.map((row) => (
            <li key={row.key} className={`${ROW_CLASS} ${row.live ? "font-medium text-foreground" : ""}`}>
              <Link to={row.path} state={{ from }} className="cursor-pointer rounded-sm">
                {row.text}
              </Link>
            </li>
          ))}
        </ol>
      )}
      {status}
    </section>
  );
}
